import {
  applyCooking,
  buildTotals,
  type CookingMethod,
  deviceTimeZone,
  gramsOfPieces,
  localDateString,
  looksCookable,
  looksLikeDrink,
  type MealSlot,
  piecesLabel,
  piecesOf,
  scaleToGrams,
} from '@gramas/shared'
import { Button, MacroPreview, NumberField, Sheet } from '@gramas/ui'
import { useEffect, useMemo, useState } from 'react'
import type { LocalFood } from '../../db/dexie.js'
import {
  foodDisplayName,
  foodPer100g,
  logAdHoc,
  logCookingOil,
  logFood,
  slotFor,
} from '../../db/repo.js'
import { logContext } from '../../sass/context.js'
import { maybeLogToast } from '../../sass/toast.js'
import { announce } from '../../shell/toastStore.js'
import { syncNow } from '../../sync/engine.js'
import { COOKING_LABEL, CookingPicker } from './CookingPicker.js'
import { DrinkExtrasSection, type Extras, extraLines, extraSummary } from './DrinkExtras.js'
import { QuantityPresets } from './QuantityPresets.js'
import { SlotPicker } from './SlotPicker.js'

export interface LogFoodSheetProps {
  open: boolean
  food: LocalFood | null
  onOpenChange: (open: boolean) => void
  onLogged?: () => void
  /** Opens the correction sheet. Absent where editing makes no sense. */
  onEdit?: (food: LocalFood) => void
  date?: string
}

/** Quantity and meal for a food that is already identified. Three taps to done. */
export function LogFoodSheet({
  open,
  food,
  onOpenChange,
  onLogged,
  onEdit,
  date,
}: LogFoodSheetProps) {
  const [slot, setSlot] = useState<MealSlot>(() => slotFor(null, 'g'))
  const [grams, setGrams] = useState<number | null>(100)
  const [extras, setExtras] = useState<Extras>(new Map())
  const [method, setMethod] = useState<CookingMethod>('raw')
  const unit = food?.unit ?? 'g'
  const name = food ? foodDisplayName(food) : ''
  const drinkable = food != null && looksLikeDrink(name, food.unit)
  const cookable = food != null && looksCookable(name)

  // A food people count opens on one of them, not on 100 g of one.
  const pieceWeightG = food?.pieceWeightG ?? null
  const pieces = pieceWeightG != null && grams != null ? piecesOf(grams, pieceWeightG) : null

  useEffect(() => {
    if (open && food) {
      setSlot(slotFor(null, food.unit, foodDisplayName(food)))
      setGrams(food.pieceWeightG ?? food.servingSizeG ?? 100)
      setExtras(new Map())
      setMethod('raw')
    }
  }, [open, food])

  const added = useMemo(() => buildTotals(extraLines(extras)), [extras])

  const preview = useMemo(
    () => (food && grams ? scaleToGrams(foodPer100g(food), grams) : null),
    [food, grams],
  )

  /**
   * The scanned packet gets the same cooking question as a searched one.
   *
   * This is where raw chicken with a barcode actually lands, and it was the one
   * path with no way to say it went in a pan — which made the feature look
   * absent rather than merely narrow.
   */
  const cooked =
    food && grams && unit === 'g' ? applyCooking(foodPer100g(food), grams, method) : null

  const commit = async () => {
    if (!food || !grams) return
    const logDate = date ?? localDateString(new Date(), deviceTimeZone())
    // The count rides along with the weight rather than replacing it: grams stay
    // the unit every macro is derived from, and the diary reads back "2 vnt."
    // because the food says what one of them weighs.
    await logFood(food, { slot, quantityG: grams, logDate, servings: pieces })
    if (cooked) await logCookingOil(COOKING_LABEL[method], cooked, { slot, quantityG: 0, logDate })
    // The sugar goes in as its own row rather than being folded into the
    // coffee: the food's snapshot has to keep meaning what the label says, and
    // a row that reads "+ cukrus ×2" is one you can delete when you misremember.
    if (added.weightG > 0) {
      await logAdHoc(extraSummary(extras).replace(/^ \+ /, '').trim(), added.totals, {
        slot,
        quantityG: added.weightG,
        logDate,
      })
    }
    void syncNow()
    onOpenChange(false)
    onLogged?.()
    // The ring filling on Today is still the confirmation. The voice adds an
    // occasional remark on top; when it speaks, the live region already reads
    // the toast, so the announce() would be the same news twice.
    const kcal = Math.round((cooked?.kcal ?? preview?.kcal ?? 0) + added.totals.kcal)
    const spoke = maybeLogToast(await logContext(logDate, slot, kcal, foodDisplayName(food)))
    if (!spoke && preview) announce(`Įrašyta ${kcal} kcal`)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={food ? foodDisplayName(food) : ''}
      description={food ? `${Math.round(food.kcal)} kcal / 100 ${unit}` : undefined}
      footer={
        <Button variant="primary" size="lg" block onClick={() => void commit()} disabled={!grams}>
          Įrašyti{' '}
          {preview ? `${Math.round((cooked?.kcal ?? preview.kcal) + added.totals.kcal)} kcal` : ''}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        <SlotPicker value={slot} onChange={setSlot} />
        {pieceWeightG != null ? (
          <>
            <NumberField
              label="Kiek vienetų"
              suffix="vnt."
              value={pieces}
              onChange={(next) => setGrams(next == null ? null : gramsOfPieces(next, pieceWeightG))}
            />
            <p className="tnum -mt-2 text-xs text-mute">
              {piecesLabel(pieces ?? 0)} · {Math.round(grams ?? 0)} g · vienas apie{' '}
              {Math.round(pieceWeightG)} g
            </p>
          </>
        ) : (
          <NumberField label="Kiekis" suffix={unit} value={grams} onChange={setGrams} />
        )}
        <QuantityPresets
          unit={unit}
          value={grams}
          servingSizeG={food?.servingSizeG ?? null}
          pieceWeightG={pieceWeightG}
          onPick={setGrams}
        />
        {unit === 'g' && (
          <CookingPicker
            key={food?.id}
            value={method}
            onChange={setMethod}
            cooked={cooked}
            defaultOpen={cookable}
          />
        )}
        {preview && (
          <MacroPreview
            kcal={preview.kcal}
            proteinG={preview.protein}
            carbsG={preview.carbs}
            fatG={preview.fat}
            extended={{
              sugarsG: preview.sugars ?? null,
              satFatG: preview.satFat ?? null,
              fiberG: preview.fiber ?? null,
              saltG: preview.salt ?? null,
            }}
          />
        )}

        {drinkable && (
          <div className="-mx-4">
            <DrinkExtrasSection extras={extras} onChange={setExtras} />
          </div>
        )}

        {onEdit && food && (
          <button
            type="button"
            onClick={() => onEdit(food)}
            className="self-start text-sm text-accent-text"
          >
            Taisyti šio produkto duomenis
          </button>
        )}
      </div>
    </Sheet>
  )
}
